import { useId, useRef } from "react";

export const CONNECTION_CONTEXTS = ["professional", "personal", "all"];

const CONTEXT_LABELS = {
  professional: "Professional",
  personal: "Personal",
  all: "All",
};

/**
 * View-level context control for the rolodex, detail panel, and map.
 *
 * `value` is "professional", "personal", or "all". `counts`, when supplied,
 * is an object keyed by those values. Arrow keys move and select in one step.
 */
export default function ContextSwitcher({
  value = "all",
  onChange,
  counts,
  label = "Rolodex view",
  hideLabel = false,
  disabled = false,
  className = "",
}) {
  const generatedId = useId();
  const labelId = `${generatedId}-label`;
  const buttonsRef = useRef([]);
  const selected = CONNECTION_CONTEXTS.includes(value) ? value : "all";

  const choose = (index) => {
    if (disabled) return;
    const next = CONNECTION_CONTEXTS[index];
    onChange?.(next);
    buttonsRef.current[index]?.focus();
  };

  const handleKeyDown = (event, index) => {
    let nextIndex = null;
    if (event.key === "ArrowRight" || event.key === "ArrowDown") {
      nextIndex = (index + 1) % CONNECTION_CONTEXTS.length;
    } else if (event.key === "ArrowLeft" || event.key === "ArrowUp") {
      nextIndex = (index - 1 + CONNECTION_CONTEXTS.length) % CONNECTION_CONTEXTS.length;
    } else if (event.key === "Home") {
      nextIndex = 0;
    } else if (event.key === "End") {
      nextIndex = CONNECTION_CONTEXTS.length - 1;
    }

    if (nextIndex !== null) {
      event.preventDefault();
      choose(nextIndex);
    }
  };

  return (
    <div className={`context-switcher ${className}`.trim()}>
      <span
        id={labelId}
        className={hideLabel ? "context-visually-hidden" : "context-switcher-label"}
      >
        {label}
      </span>
      <div
        className="context-switcher-track"
        role="radiogroup"
        aria-labelledby={labelId}
        aria-disabled={disabled || undefined}
      >
        {CONNECTION_CONTEXTS.map((context, index) => {
          const isSelected = selected === context;
          const count = counts?.[context];
          return (
            <button
              key={context}
              ref={(node) => { buttonsRef.current[index] = node; }}
              type="button"
              role="radio"
              aria-checked={isSelected}
              tabIndex={isSelected ? 0 : -1}
              className={`context-switcher-option context-${context}${isSelected ? " is-active" : ""}`}
              onClick={() => choose(index)}
              onKeyDown={(event) => handleKeyDown(event, index)}
              disabled={disabled}
            >
              <span className="context-switcher-mark" aria-hidden="true" />
              <span>{CONTEXT_LABELS[context]}</span>
              {Number.isFinite(count) && (
                <span className="context-switcher-count" aria-label={`${count} connections`}>
                  {count}
                </span>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}

